import { getStorageData, saveStorageData, StorageData } from "@/storage";

/**
 * User state interface for type safety
 */
export interface UserStates {
  fontSize?: number;
  contrast?: string | boolean;
  [key: string]: any; // Allow dynamic properties for extensibility
}

/**
 * User settings interface
 */
export interface UserSettings {
  lang: string | undefined;
  position: string | undefined;
  states: UserStates;
}

/**
 * Default user settings
 */
const DEFAULT_USER_SETTINGS: UserSettings = {
  lang: undefined,
  position: undefined,
  states: {}
};

/**
 * Global user settings object
 */
export let userSettings: UserSettings = { ...DEFAULT_USER_SETTINGS };

/**
 * Storage key for user settings
 */
export const STORAGE_KEY = "cya11y-user-settings";

/**
 * Sets user state settings and saves to storage
 * @param state - Partial state object to merge with existing states
 * @returns void
 */
export function setUserStateSettings(state: Partial<UserStates>): void {
  userSettings.states = {
    ...userSettings.states,
    ...state
  };

  saveUserSettings();
}

/**
 * Saves current user settings to storage
 * @returns void
 */
export function saveUserSettings(): void {
  try {
    saveStorageData(STORAGE_KEY, userSettings);
  } catch (error) {
    console.error('Failed to save user settings:', error);
  }
}

/**
 * Retrieves saved user settings from storage
 * @returns Saved user settings or default settings if none found
 */
export function getSavedUserSettings(): UserSettings {
  try {
    const savedSettings = getStorageData(STORAGE_KEY) as UserSettings;
    
    // Validate and merge with defaults if needed
    if (savedSettings && typeof savedSettings === 'object') {
      return {
        ...DEFAULT_USER_SETTINGS,
        ...savedSettings,
        states: {
          ...DEFAULT_USER_SETTINGS.states,
          ...savedSettings.states
        }
      };
    }
  } catch (error) {
    console.error('Failed to load saved user settings:', error);
  }
  
  return { ...DEFAULT_USER_SETTINGS };
}

/**
 * Resets user settings to defaults
 * @returns void
 */
export function resetUserSettings(): void {
  userSettings = { ...DEFAULT_USER_SETTINGS };
  saveUserSettings();
}